import React, { useState, useRef, useContext, useEffect } from 'react'
import { useIsMobile, useTrans } from 'hooks'
import { useSelector } from 'react-redux'
import { getFreeShippingRest, getShippingCountDown, priceNotation } from 'utils/safe'
import { CartContext, initialCart } from 'utils/context'
import { gql, useLazyQuery, useQuery } from '@apollo/client'
import { PRODOTTI_CORRELATI_CART, CONFEZIONI_CORRELATE_CART } from 'utils/queries'
import { Alert, LocalShipping, Info } from 'components/atoms/Icons'
import { Loader, NotificationBar } from 'components/atoms'
import { RiepilogoOrdine, ProdottoCardCarrello } from 'components/molecules'
import {
  Page,
  RegaloForm,
  RegaloConfezioni,
  ProdottiCorrelati,
  PromoCodeForm,
} from 'components/organisms'
import {
  trackingAddRemoveFromCart,
  trackingCheckout,
  trackingRemoveProductFromCart,
} from 'utils/gtm'
import { executeIfInitialized, trackConnectifCart } from 'utils/connectif'

const CartTemplate = ({ pagina }) => {
  const context = useContext(CartContext)
  const [viewLoading, setviewLoading] = useState(false)
  const isLoading = context ? context.isLoading : false
  const cart = context ? context.cart : initialCart
  const { addMessaggio } = context
  const [prodottiCorrelati, setProdottiCorrelati] = useState([])
  const [confezioniCorrelate, setConfezioniCorrelate] = useState(null)
  const [errorsList, setErrorsList] = useState([])
  const [trackingSent, setTrackingSent] = useState(false)
  const [open, setOpen] = useState(false)
  const isMobile = useIsMobile()
  const [initialLoading, setInitialLoading] = useState(true)

  const t = useTrans()
  const confezioniRef = useRef()
  const [getCorrelatiData, { data: dataProdottiCorrelati, loading: loadingProdottiCorrelati }] =
    useLazyQuery(
      gql`
        ${PRODOTTI_CORRELATI_CART}
      `
    )
  const [getData, { data: dataConfezioniCorrelate, loading: loadingConfezioniCorrelate }] =
    useLazyQuery(
      gql`
        ${CONFEZIONI_CORRELATE_CART}
      `
    )

  const { menu } = useSelector((state: any) => state.menu)
  const notificationBarItem = menu?.edges?.filter(
    ({ node }) => node?.key === 'notificationcart' && node?.attivo === true
  )?.[0]?.node
  const notificationBarItemRiceviDomani = menu?.edges?.filter(
    ({ node }) => node?.key === 'notificationcart_ricevi_domani' && node?.attivo === true
  )?.[0]?.node

  const shippingCountDown = getShippingCountDown()
  const freeShippingRest = getFreeShippingRest(
    cart.limiteFreeShipping,
    cart.totaleProdottiScontato,
    cart.promoApplicata
  )

  const onQuantitaChange = (item, quantita) => {
    context
      .addToCart(item.prodotto, quantita - item.quantita, false)
      .then(({ status, shortMessage, quantitaMax }) => {
        setInitialLoading(true)
        if (shortMessage && !errorsList.some(({ pk }) => pk === item.pk)) {
          setErrorsList(errorsList.concat([{ pk: item.pk, message: shortMessage }]))
        }
        if (!shortMessage && errorsList.some(({ pk }) => pk === item.pk)) {
          setErrorsList(errorsList.filter(({ pk }) => pk !== item.pk))
        }
        if (status && quantitaMax) {
          trackingAddRemoveFromCart(item, quantitaMax - item.quantita)
          if (cart) executeIfInitialized(trackConnectifCart, cart)
        } else if (status) {
          trackingAddRemoveFromCart(item, quantita - item.quantita)
          if (cart) executeIfInitialized(trackConnectifCart, cart)
        }
      })
  }

  const onRemove = (item) => {
    context.removeFromCart(item.prodotto, false)
    trackingRemoveProductFromCart(item)
    setInitialLoading(true)
  }

  const [refetched, setRefetched] = useState(false)
  const [includiMessaggio, setIncludiMessaggio] = useState(false)
  const [messaggio, setMessaggio] = useState('')
  const [messaggioFirma, setMessaggioFirma] = useState('')

  const onChangeIncludiMessaggio = (value) => {
    setIncludiMessaggio(value)
    setMessaggio('')
    setMessaggioFirma('')
  }

  useEffect(() => {
    if (cart && !isLoading) {
      if (!refetched) {
        setIncludiMessaggio(cart.includiMessaggio)
        setMessaggio(cart.messaggio || '')
        setMessaggioFirma(cart.messaggioFirma || '')
        setRefetched(true)
        //Aggiungere controllo solo per desktop
        if (!isMobile) setOpen(true)
      }
    }
  }, [cart, refetched])

  useEffect(() => {
    if (!loadingProdottiCorrelati && !dataProdottiCorrelati) {
      getCorrelatiData()
      setInitialLoading(false)
    }
  }, [loadingProdottiCorrelati, dataProdottiCorrelati])

  useEffect(() => {
    if (includiMessaggio && confezioniCorrelate === null && !loadingConfezioniCorrelate) {
      getData()
    }
  }, [includiMessaggio, confezioniCorrelate, loadingConfezioniCorrelate])

  useEffect(() => {
    if (dataProdottiCorrelati) {
      setProdottiCorrelati(dataProdottiCorrelati.cart?.correlati)
    }
  }, [dataProdottiCorrelati])

  useEffect(() => {
    if (dataConfezioniCorrelate) {
      setConfezioniCorrelate(dataConfezioniCorrelate.cart?.confezioniCorrelate)
    }
  }, [dataConfezioniCorrelate])

  useEffect(() => {
    if (cart && !isLoading && !trackingSent) {
      trackingCheckout(cart, 1, '')
      setTrackingSent(true)
      executeIfInitialized(trackConnectifCart, cart)
    }
  }, [cart])

  useEffect(() => {
    if (initialLoading && isLoading) {
      setviewLoading(true)
    }
    if (isLoading) {
      setInitialLoading(false)
    }
    if (!isLoading) {
      setviewLoading(false)
    }
  }, [isLoading, initialLoading])

  return (
    <Page
      hideBreadcrumbs
      pagina={pagina}
      customContent={
        <div className="cart-template__head">
          <h1 className="cart-template__title">{t('Carrello')}</h1>
          {cart.numProdotti > 0 && (
            <span className="cart-template__num-prodotti">
              (
              {cart.numProdotti === 1
                ? t('1 prodotto')
                : t('{0} prodotti', [cart.numProdotti.toString()])}
              )
            </span>
          )}
        </div>
      }
      className="cart-template"
    >
      <div className="cart-template__layout">
        <div className="cart-template__main">
          {freeShippingRest > 0 && (
            <NotificationBar
              icon={<Info />}
              label={t('Aggiungi {0} di prodotti per ottenere la spedizione gratuita.', [
                priceNotation(freeShippingRest),
              ])}
              className="cart-template__shipping-notification-bar"
            />
          )}
          {viewLoading ? (
            <Loader></Loader>
          ) : (
            <div className="cart-template__list">
              {cart.items.map((item) => (
                <ProdottoCardCarrello
                  prodotto={item.prodotto.prodotto}
                  quantita={item.quantita}
                  omaggio={item.omaggio}
                  onQuantitaChange={(newValue, action) => {
                    onQuantitaChange(item, newValue)
                  }}
                  onRemove={() => onRemove(item)}
                  key={`cart-template__prodotto-card-carrello__${item.pk}`}
                  message={
                    errorsList.some(({ pk }) => pk === item.pk)
                      ? errorsList.find(({ pk }) => pk === item.pk).message
                      : ''
                  }
                  bordered
                />
              ))}
            </div>
          )}
          <RegaloForm
            className="cart-template__regalo-form"
            open={includiMessaggio}
            setOpen={onChangeIncludiMessaggio}
            confezioniRef={confezioniRef}
            messaggio={messaggio}
            onChangeMessaggio={setMessaggio}
            messaggioFirma={messaggioFirma}
            onChangeMessaggioFirma={setMessaggioFirma}
          />
        </div>
        <div className="cart-template__sidebar">
          <RiepilogoOrdine
            ordine={{
              totaleProdotti: cart.totaleProdottiScontato,
              speseSpedizione: cart.sistemaSpedizione?.prezzoScontato
                ? cart.sistemaSpedizione.prezzoScontato
                : 0,
              scontoAssoluto: cart.scontoAssoluto,
              totale: cart.totaleScontato,
              promo: cart.promoApplicata,
            }}
            cart={cart.numProdotti !== 0}
            setMessaggioRegalo={addMessaggio}
            messaggioRegalo={{
              includiMessaggio,
              messaggio,
              messaggioFirma,
            }}
            accise={cart?.accise}
            iva={cart?.iva}
            estero={cart?.sistemaSpedizione?.estero}
            erroreApisEstero={cart?.erroreApisEstero}
          />
          <PromoCodeForm open={open} setOpen={setOpen} />
          {notificationBarItemRiceviDomani && shippingCountDown !== null && (
            <NotificationBar
              variant="success"
              icon={<LocalShipping />}
              label={notificationBarItemRiceviDomani.name.replace(
                '{0}',
                t(shippingCountDown.label, [
                  shippingCountDown.hours.toString(),
                  shippingCountDown.minutes.toString(),
                ])
              )}
            />
          )}
          {notificationBarItem && (
            <NotificationBar variant="alert" icon={<Alert />} label={notificationBarItem.name} />
          )}
        </div>
      </div>
      {includiMessaggio && confezioniCorrelate && confezioniCorrelate.length > 0 && (
        <div ref={confezioniRef}>
          <RegaloConfezioni
            confezioni={confezioniCorrelate}
            className="cart-template__regalo-confezioni"
          />
        </div>
      )}
      {prodottiCorrelati && prodottiCorrelati.length > 0 && (
        <ProdottiCorrelati
          title={t('Aggiungi prodotti al tuo ordine')}
          className="cart-template__correlati"
          prodotti={prodottiCorrelati}
        />
      )}
    </Page>
  )
}

export default CartTemplate
